'use client';

import '@/app/ui/papa/cardapio/cardapio.css';
import { Categoria } from '@/app/lib/papa/categoria/definitions';
import {
    Produto,
    FAIXAS_ETARIAS,
    TAGS_NUTRICIONAIS,
    getFaixaEtariaInfo,
    getTagNutricionalInfo
} from '@/app/lib/papa/definitions';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import { useCart } from '@/app/lib/papa/cardapio/cart-context';
import SizeSelectionModal from './size-selection-modal';
import CartBar from './cart-bar';
import CartDrawer from './cart-drawer';
import { Add, Remove, ShoppingBagOutlined as ShoppingBagIcon, Close } from '@mui/icons-material';
import toast, { Toaster } from 'react-hot-toast';

import { HorarioFuncionamento, HorarioExcecao } from '@/app/lib/definitions';
import { getStoreStatus } from '@/app/lib/papa/horarios/utils';
import { TwoWheeler, Storefront } from '@mui/icons-material';
import { obterClienteLocalStorage, limparClienteLocalStorage, ClienteSalvo } from '@/app/lib/papa/cardapio/clienteStorage';
import { buscarSaldoClienteAction } from '@/app/lib/papa/fidelidade/fidelidadeActions';

function formatCurrency(value: number) {
    return 'R$ ' + value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Cardapio({
    initialCategorias,
    horarios = [],
    excecoes = []
}: {
    initialCategorias: Categoria[];
    horarios?: HorarioFuncionamento[];
    excecoes?: HorarioExcecao[];
}) {
    const storeStatus = getStoreStatus(horarios, excecoes);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFaixa, setSelectedFaixa] = useState<string>('todas');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [selectedProduto, setSelectedProduto] = useState<Produto | null>(null);
    const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
    const { addToCart, cart, updateQuantity } = useCart();
    const [expandedDescriptions, setExpandedDescriptions] = useState<Record<string, boolean>>({});
    const [activeCategoria, setActiveCategoria] = useState<string | null>(null);
    const categoryRefs = useRef(new Map<string, HTMLElement>());

    // Cliente Reconhecido via LocalStorage
    const [clienteSalvo, setClienteSalvo] = useState<ClienteSalvo | null>(null);

    useEffect(() => {
        const dados = obterClienteLocalStorage();
        if (dados) {
            setClienteSalvo(dados);
            if (dados.clienteId) {
                buscarSaldoClienteAction(dados.clienteId)
                    .then(res => {
                        const saldo = Number(res?.saldo_cashback || 0);
                        setClienteSalvo(prev => (prev ? { ...prev, saldoCashback: saldo } : null));
                    })
                    .catch(() => {});
            }
        }
    }, []);

    const handleLimparClienteCardapio = () => {
        limparClienteLocalStorage();
        setClienteSalvo(null);
        toast('Identificação removida deste navegador.');
    };

    const toggleTagFilter = (tagId: string) => {
        setSelectedTags(prev =>
            prev.includes(tagId) ? prev.filter(t => t !== tagId) : [...prev, tagId]
        );
    };

    const clearAllFilters = () => {
        setSelectedFaixa('todas');
        setSelectedTags([]);
        setSearchTerm('');
    };

    const hasActiveFilters = selectedFaixa !== 'todas' || selectedTags.length > 0 || searchTerm.trim() !== '';

    const handleDecrementSingle = (e: React.MouseEvent, produto: Produto) => {
        e.stopPropagation();
        const quantidade = getProdutoQuantidade(produto);
        if (produto.tamanhos.length > 0) {
            updateQuantity(produto.id, produto.tamanhos[0].id, quantidade - 1);
        }
    };

    const handleIncrementSingle = (e: React.MouseEvent, produto: Produto) => {
        e.stopPropagation();
        if (produto.tamanhos.length > 0 && produto.tamanhos[0].visivel !== false) {
            const tam = produto.tamanhos[0];
            const maxEstoque = (tam.gerenciar_estoque !== false && tam.quantidade_atual !== undefined)
                ? Math.max(0, tam.quantidade_atual)
                : 999;
            const quantidade = getProdutoQuantidade(produto);

            if (maxEstoque <= 0) {
                toast.error('Item esgotado no momento.');
                return;
            }

            if (quantidade < maxEstoque) {
                addToCart(produto, tam);
            } else {
                toast.error(`Estoque máximo atingido (${maxEstoque} un)`);
            }
        }
    };

    const handleDecrementMulti = (e: React.MouseEvent, produto: Produto) => {
        e.stopPropagation();
        const itemsForProduct = cart.filter(item => item.produto.id === produto.id);
        if (itemsForProduct.length > 0) {
            const lastItem = itemsForProduct[itemsForProduct.length - 1];
            updateQuantity(produto.id, lastItem.tamanho.id, lastItem.quantity - 1);
        }
    };

    const handleIncrementMulti = (e: React.MouseEvent, produto: Produto) => {
        e.stopPropagation();
        setSelectedProduto(produto);
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveCategoria(entry.target.id);
                    }
                });
            },
            {
                rootMargin: '-180px 0px -50% 0px',
                threshold: 0,
            }
        );

        const refs = categoryRefs.current;
        refs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            refs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, []);

    const toggleDescription = (produtoId: string) => {
        setExpandedDescriptions(prevState => ({
            ...prevState,
            [produtoId]: !prevState[produtoId],
        }));
    };

    const getProdutoQuantidade = (produto: Produto) => {
        return cart.reduce((total, item) => {
            if (item.produto.id === produto.id) {
                return total + item.quantity;
            }
            return total;
        }, 0);
    };

    const handleProdutoClick = (produto: Produto) => {
        const tamanhosVisiveis = produto.tamanhos ? produto.tamanhos.filter(t => t.visivel !== false) : [];
        if (tamanhosVisiveis.length === 1) {
            const tam = tamanhosVisiveis[0];
            const maxEstoque = (tam.gerenciar_estoque !== false && tam.quantidade_atual !== undefined)
                ? Math.max(0, tam.quantidade_atual)
                : 999;
            const quantidade = getProdutoQuantidade(produto);

            if (maxEstoque <= 0) {
                toast.error('Item esgotado no momento.');
                return;
            }

            if (quantidade < maxEstoque) {
                addToCart(produto, tam);
            } else {
                toast.error(`Estoque máximo atingido (${maxEstoque} un)`);
            }
        } else if (tamanhosVisiveis.length > 1) {
            setSelectedProduto(produto);
        }
    };

    const filteredCategorias = initialCategorias
        .map(categoria => ({
            ...categoria,
            produtos: (categoria.produtos || [])
                .filter(produto => {
                    if (produto.visivel === false) return false;

                    // 1. Filtro de Texto
                    if (searchTerm && searchTerm.trim() !== '') {
                        const s = searchTerm.toLowerCase();
                        const matchesTitle = (produto.titulo || '').toLowerCase().includes(s);
                        const matchesDesc = (produto.descricao || '').toLowerCase().includes(s);
                        if (!matchesTitle && !matchesDesc) return false;
                    }

                    // 2. Filtro de Faixa Etária / Fase
                    if (selectedFaixa && selectedFaixa !== 'todas') {
                        const prodFaixa = produto.faixa_etaria;
                        if (prodFaixa && prodFaixa !== 'todas' && prodFaixa !== selectedFaixa) {
                            return false;
                        }
                    }

                    // 3. Filtro de Alergênicos / Tags Nutricionais
                    if (selectedTags.length > 0) {
                        const prodTags = Array.isArray(produto.tags_nutricionais) ? produto.tags_nutricionais : [];
                        const matchesAllTags = selectedTags.every(t => prodTags.includes(t));
                        if (!matchesAllTags) return false;
                    }

                    return true;
                })
                .map(produto => ({
                    ...produto,
                    tamanhos: produto.tamanhos?.filter(t => t.visivel !== false) || []
                }))
                .filter(produto => produto.tamanhos && produto.tamanhos.length > 0),
        }))
        .filter(categoria => categoria.produtos && categoria.produtos.length > 0);

    const totalProdutosFiltrados = filteredCategorias.reduce(
        (acc, cat) => acc + (cat.produtos?.length || 0), 0
    );

    return (
        <>
            <Toaster position="top-center" />

            {/* Header com Status da Loja em Tempo Real */}
            <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 py-2.5 shadow-2xs">
                <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-bold shadow-2xs ${storeStatus.color === 'green'
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
                            : storeStatus.color === 'amber'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                                : 'bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 border border-rose-200 dark:border-rose-800'
                            }`}>
                            <span className={`w-2 h-2 rounded-full ${storeStatus.color === 'green' ? 'bg-emerald-500 animate-pulse' : storeStatus.color === 'amber' ? 'bg-amber-500' : 'bg-rose-500'
                                }`} />
                            {storeStatus.badge}
                        </span>
                        <span className="text-slate-600 dark:text-slate-300 font-medium hidden sm:inline">
                            {storeStatus.detail}
                        </span>
                    </div>

                    <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400 font-medium">
                        <span className="inline-flex items-center gap-1">
                            <TwoWheeler fontSize="inherit" className="text-red-500" />
                            <span>Entrega</span>
                        </span>
                        <span className="text-slate-300 dark:text-slate-700">•</span>
                        <span className="inline-flex items-center gap-1">
                            <Storefront fontSize="inherit" className="text-blue-500" />
                            <span>Retirada</span>
                        </span>

                        {cart.length > 0 && (
                            <>
                                <span className="text-slate-300 dark:text-slate-700">•</span>
                                <button
                                    onClick={() => setIsCartDrawerOpen(true)}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs shadow-xs transition-all active:scale-95 cursor-pointer"
                                >
                                    <ShoppingBagIcon fontSize="inherit" />
                                    <span>Sacola ({cart.reduce((acc, i) => acc + i.quantity, 0)})</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Banner de Reconhecimento do Cliente via LocalStorage */}
            {clienteSalvo && (
                <div className="bg-gradient-to-r from-red-500/10 via-amber-500/10 to-transparent dark:from-red-950/40 dark:via-amber-950/20 border-b border-red-200/50 dark:border-red-900/40 px-4 py-2 text-xs">
                    <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 overflow-hidden">
                            <span className="text-base shrink-0">👋</span>
                            <div className="truncate flex items-center gap-2">
                                <span className="font-bold text-slate-800 dark:text-slate-100">
                                    Olá, {clienteSalvo.nome ? clienteSalvo.nome.split(' ')[0] : 'Cliente'}!
                                </span>
                                {clienteSalvo.saldoCashback !== undefined && clienteSalvo.saldoCashback > 0 ? (
                                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 font-extrabold text-[11px] border border-emerald-300 dark:border-emerald-800">
                                        Cashback: {formatCurrency(clienteSalvo.saldoCashback)}
                                    </span>
                                ) : (
                                    <span className="text-slate-500 dark:text-slate-400 hidden sm:inline text-[11px]">
                                        Seus dados e endereços estão salvos para um pedido rápido.
                                    </span>
                                )}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleLimparClienteCardapio}
                            className="text-[11px] font-bold text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400 underline cursor-pointer shrink-0"
                            title="Trocar de cliente ou desvincular este dispositivo"
                        >
                            Trocar
                        </button>
                    </div>
                </div>
            )}

            <div className="sticky-header-cardapio">
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Buscar no cardápio por nome, ingredientes ou fase..."
                        className="search-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* FILTROS RÁPIDOS POR FASE DE DESENVOLVIMENTO (Idade) */}
                <div className="my-2">
                    <div className="flex items-center gap-1.5 overflow-x-auto py-1 no-scrollbar">
                        <button
                            type="button"
                            onClick={() => setSelectedFaixa('todas')}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 flex items-center gap-1 cursor-pointer ${selectedFaixa === 'todas'
                                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-xs'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            <span>✨</span>
                            <span>Todas as Fases</span>
                        </button>
                        {FAIXAS_ETARIAS.filter(f => f.id !== 'todas').map((faixa) => {
                            const isSelected = selectedFaixa === faixa.id;
                            return (
                                <button
                                    key={faixa.id}
                                    type="button"
                                    onClick={() => setSelectedFaixa(isSelected ? 'todas' : faixa.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all shrink-0 flex items-center gap-1.5 cursor-pointer shadow-2xs border ${isSelected
                                        ? `${faixa.badgeBg} ${faixa.badgeBorder} ${faixa.badgeText} ring-2 ring-current font-black`
                                        : 'bg-slate-50 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-300'
                                        }`}
                                >
                                    <span>{faixa.emoji}</span>
                                    <span>{faixa.shortLabel}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* FILTROS RÁPIDOS POR ALERGÊNICOS & PROPRIEDADES NUTRICIONAIS */}
                <div className="my-2.5 pt-0.5">
                    <div className="flex items-center gap-2 overflow-x-auto py-1.5 no-scrollbar">
                        {TAGS_NUTRICIONAIS.map((tag) => {
                            const isSelected = selectedTags.includes(tag.id);
                            return (
                                <button
                                    key={tag.id}
                                    type="button"
                                    onClick={() => toggleTagFilter(tag.id)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all shrink-0 flex items-center gap-1.5 cursor-pointer border ${isSelected
                                        ? `${tag.badgeBg} ${tag.badgeBorder} ${tag.badgeText} font-bold ring-2 ring-current shadow-2xs`
                                        : 'bg-white dark:bg-slate-800/90 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-slate-300 shadow-2xs'
                                        }`}
                                >
                                    <span className="text-sm">{tag.emoji}</span>
                                    <span>{tag.shortLabel}</span>
                                    {isSelected && <span className="text-[10px] ml-0.5 font-bold opacity-80">✕</span>}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* BARRA DE FILTROS ATIVOS & RESULTADOS */}
                {hasActiveFilters && (
                    <div className="flex items-center justify-between py-1.5 px-3 rounded-xl bg-slate-100 dark:bg-slate-800/60 text-xs my-2 border border-slate-200/80 dark:border-slate-700">
                        <span className="text-slate-600 dark:text-slate-300 font-medium">
                            <strong className="text-slate-900 dark:text-white font-bold">{totalProdutosFiltrados}</strong> {totalProdutosFiltrados === 1 ? 'produto encontrado' : 'produtos encontrados'}
                        </span>
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            className="text-red-600 dark:text-red-400 hover:underline font-bold flex items-center gap-1 cursor-pointer text-xs"
                        >
                            <Close style={{ fontSize: 13 }} />
                            <span>Limpar Filtros</span>
                        </button>
                    </div>
                )}

                <nav className="category-tabs">
                    {filteredCategorias.map((categoria) => (
                        <a key={categoria.id} href={`#${categoria.id}`}
                            className={`category-tab ${activeCategoria === categoria.id ? 'active' : ''}`}>
                            {categoria.nome}
                        </a>
                    ))}
                </nav>
            </div>

            <div className="pt-4 pb-10 px-4 md:px-6">

                {filteredCategorias.length === 0 && (
                    <div className="no-results py-12 text-center">
                        <p className="text-base font-bold text-slate-700 dark:text-slate-300">
                            Nenhum produto encontrado com os filtros selecionados.
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                            Tente desmarcar alguns filtros de fase ou alergênicos para visualizar mais opções.
                        </p>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-full text-xs font-bold shadow-sm transition-all cursor-pointer"
                            >
                                Limpar Todos os Filtros
                            </button>
                        )}
                    </div>
                )}

                {filteredCategorias.map((categoria) => (
                    <section
                        key={categoria.id}
                        id={categoria.id}
                        className="categoria-section"
                        ref={el => el ? categoryRefs.current.set(categoria.id, el) : categoryRefs.current.delete(categoria.id)}>
                        <h2 className="categoria-title">{categoria.nome}</h2>
                        <div className="produtos-grid">

                            {categoria.produtos?.map((produto: Produto) => {
                                const quantidade = getProdutoQuantidade(produto);
                                const isExpanded = expandedDescriptions[produto.id];
                                const shouldTruncate = produto.descricao.length > 100;

                                return (
                                    <div key={produto.id} className="bg-white dark:bg-card-dark rounded-xl shadow-lg hover:shadow-hover transition-shadow duration-300 overflow-hidden flex flex-col justify-between border border-gray-100 dark:border-gray-700 mb-2">
                                        <div
                                            onClick={() => setSelectedProduto(produto)}
                                            className="p-4 md:p-6 cursor-pointer"
                                        >
                                            {/* Badges de Fase, Nutrição e Kit */}
                                            <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                                {produto.is_kit && (
                                                    <span className="px-2 py-0.5 rounded-full font-bold text-[10px] border flex items-center gap-1 bg-amber-500 text-white border-amber-600 shadow-xs">
                                                        <span>🎁</span>
                                                        <span>
                                                            Kit com {Array.isArray(produto.itens) ? produto.itens.reduce((acc: number, i: any) => acc + Number(i.quantidade || 1), 0) : ''} itens
                                                        </span>
                                                    </span>
                                                )}

                                                {(() => {
                                                    const faixaInfo = getFaixaEtariaInfo(produto.faixa_etaria);
                                                    if (!faixaInfo) return null;
                                                    return (
                                                        <span className={`px-2 py-0.5 rounded-full font-bold text-[10px] border flex items-center gap-1 ${faixaInfo.badgeBg} ${faixaInfo.badgeText} ${faixaInfo.badgeBorder}`}>
                                                            <span>{faixaInfo.emoji}</span>
                                                            <span>{faixaInfo.shortLabel}</span>
                                                        </span>
                                                    );
                                                })()}

                                                {Array.isArray(produto.tags_nutricionais) && produto.tags_nutricionais.map((tagId) => {
                                                    const tagInfo = getTagNutricionalInfo(tagId);
                                                    if (!tagInfo) return null;
                                                    return (
                                                        <span
                                                            key={tagId}
                                                            className={`px-2 py-0.5 rounded-full font-bold text-[10px] border flex items-center gap-0.5 ${tagInfo.badgeBg} ${tagInfo.badgeText} ${tagInfo.badgeBorder}`}
                                                        >
                                                            <span>{tagInfo.emoji}</span>
                                                            <span>{tagInfo.shortLabel}</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>

                                            <div className="flex items-start">
                                                <div className="flex-1 pr-1 md:pr-4">
                                                    <h3 className="md:text-lg font-bold text-gray-900 dark:text-white mb-2 grid hover:text-[#EF4444] transition-colors">
                                                        {produto.titulo}
                                                    </h3>
                                                    <p className="produto-descricao">
                                                        {shouldTruncate && !isExpanded
                                                            ? produto.descricao.substring(0, 100) + ' ...'
                                                            : produto.descricao}
                                                        {shouldTruncate && (
                                                            <a onClick={() => toggleDescription(produto.id)} className="read-more">
                                                                <br />{isExpanded ? 'Leia menos' : 'Leia mais'}
                                                            </a>
                                                        )}
                                                    </p>

                                                    {/* Resumo dos Itens do Kit */}
                                                    {produto.is_kit && Array.isArray(produto.itens) && produto.itens.length > 0 && (
                                                        <div className="mt-2 text-[11px] font-medium text-amber-800 dark:text-amber-300 bg-amber-50/70 dark:bg-amber-950/30 p-2 rounded-lg border border-amber-200/70 dark:border-amber-800/40">
                                                            <span className="font-bold">Itens inclusos:</span>{' '}
                                                            {produto.itens.map((i: any) => `${i.quantidade}x ${i.produto_titulo}`).join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                                {produto.foto_url && (
                                                    <div
                                                        onClick={(e) => { e.stopPropagation(); setSelectedProduto(produto); }}
                                                        className="relative cursor-pointer group/img shrink-0"
                                                        title={produto.fotos && produto.fotos.length > 1 ? `Ver todas as ${produto.fotos.length} fotos` : 'Ver detalhes do produto'}
                                                    >
                                                        <div className='hidden md:flex md:justify-end'>
                                                            <Image
                                                                unoptimized
                                                                src={produto.foto_url}
                                                                alt={produto.titulo}
                                                                width={120}
                                                                height={120}
                                                                className="produto-imagem rounded-xl object-cover transition-transform duration-200 group-hover/img:scale-105"
                                                            />
                                                        </div>
                                                        <div className='flex justify-center md:hidden'>
                                                            <Image
                                                                unoptimized
                                                                src={produto.foto_url}
                                                                alt={produto.titulo}
                                                                width={80}
                                                                height={80}
                                                                className="produto-imagem-sm rounded-xl object-cover transition-transform duration-200 group-hover/img:scale-105"
                                                            />
                                                        </div>
                                                        {produto.fotos && produto.fotos.length > 1 && (
                                                            <div className="absolute bottom-1 right-1 bg-black/75 backdrop-blur-xs text-white text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-0.5 shadow-sm">
                                                                <span>📷 {produto.fotos.length}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>                                            {produto.tamanhos && produto.tamanhos.length > 0 && (
                                                <div className="mt-4 space-y-3 divide-y divide-gray-100 dark:divide-gray-700">
                                                    {produto.tamanhos.map(tamanho => {
                                                        const poucasUnidades = tamanho.gerenciar_estoque !== false && (tamanho.quantidade_atual ?? 0) <= 5 && (tamanho.quantidade_atual ?? 0) > 0 && tamanho.visivel !== false;

                                                        return (
                                                            <div className="flex-none grid md:flex md:justify-between items-center pt-2" key={tamanho.id}>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                        {tamanho.nome}
                                                                    </span>
                                                                    {poucasUnidades && (
                                                                        <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/60 px-1.5 py-0.5 rounded-md border border-amber-200 dark:border-amber-800 shadow-2xs">
                                                                            ⚡ Restam {tamanho.quantidade_atual} un
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {tamanho.visivel === false && <span className="md:ml-2 text-sm font-medium text-red-500">Esgotado</span>}
                                                                {tamanho.visivel !== false && <span className="text-sm font-bold text-[#EF4444]">
                                                                    {tamanho.valor_desconto && tamanho.valor_desconto > 0 && tamanho.valor_desconto !== tamanho.valor ? (
                                                                        <>
                                                                            <span className="produto-valor-original whitespace-nowrap">{formatCurrency(tamanho.valor)}</span>
                                                                            <span className="produto-valor-desconto whitespace-nowrap">{formatCurrency(tamanho.valor_desconto)}</span>
                                                                        </>
                                                                    ) : (
                                                                        <span className="produto-tamanho-valor whitespace-nowrap">{formatCurrency(tamanho.valor)}</span>
                                                                    )}
                                                                </span>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                        <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2.5 flex justify-between items-center border-t border-gray-100 dark:border-gray-700 min-h-[50px]">
                                            {quantidade > 0 ? (
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                                                    {quantidade} {quantidade === 1 ? 'no carrinho' : 'no carrinho'}
                                                </span>
                                            ) : (
                                                <span />
                                            )}

                                            {(() => {
                                                const tamanhosVisiveis = produto.tamanhos ? produto.tamanhos.filter(t => t.visivel !== false) : [];

                                                if (tamanhosVisiveis.length === 0) {
                                                    return (
                                                        <span className="px-3 py-1 text-xs font-bold text-red-600 bg-red-50 dark:bg-red-950/40 rounded-full border border-red-200 dark:border-red-900">
                                                            Esgotado
                                                        </span>
                                                    );
                                                }

                                                if (tamanhosVisiveis.length === 1) {
                                                    const unicoTamanho = tamanhosVisiveis[0];
                                                    const maxEstoque = (unicoTamanho.gerenciar_estoque !== false && unicoTamanho.quantidade_atual !== undefined)
                                                        ? Math.max(0, unicoTamanho.quantidade_atual)
                                                        : 999;
                                                    const isEsgotado = unicoTamanho.gerenciar_estoque !== false && maxEstoque <= 0;
                                                    const isMaxReached = quantidade >= maxEstoque;

                                                    if (isEsgotado) {
                                                        return (
                                                            <span className="px-3 py-1 text-xs font-bold text-red-600 bg-red-50 dark:bg-red-950/40 rounded-full border border-red-200 dark:border-red-900">
                                                                Esgotado
                                                            </span>
                                                        );
                                                    }

                                                    if (quantidade === 0) {
                                                        return (
                                                            <button
                                                                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold text-xs shadow-sm transition-all active:scale-95 cursor-pointer"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    addToCart(produto, unicoTamanho);
                                                                }}
                                                            >
                                                                <Add style={{ fontSize: '16px' }} />
                                                                <span>Adicionar</span>
                                                            </button>
                                                        );
                                                    }
                                                    return (
                                                        <div className="flex items-center bg-white dark:bg-gray-700 rounded-full p-0.5 shadow-sm border border-gray-200 dark:border-gray-600">
                                                            <button
                                                                className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 flex items-center justify-center transition-colors active:scale-90 cursor-pointer"
                                                                onClick={(e) => handleDecrementSingle(e, produto)}
                                                                title="Diminuir quantidade"
                                                            >
                                                                <Remove style={{ fontSize: '15px' }} />
                                                            </button>
                                                            <span className="px-2.5 font-bold text-sm text-gray-900 dark:text-white min-w-[20px] text-center">
                                                                {quantidade}
                                                            </span>
                                                            <button
                                                                disabled={isMaxReached}
                                                                className={`w-7 h-7 rounded-full text-white flex items-center justify-center transition-colors shadow-xs active:scale-90 ${isMaxReached
                                                                    ? 'bg-slate-300 dark:bg-slate-600 text-slate-500 cursor-not-allowed opacity-50'
                                                                    : 'bg-[#EF4444] hover:bg-[#DC2626] cursor-pointer'
                                                                    }`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    if (!isMaxReached) {
                                                                        addToCart(produto, unicoTamanho);
                                                                    } else {
                                                                        toast.error(`Estoque máximo atingido (${maxEstoque} un)`);
                                                                    }
                                                                }}
                                                                title={isMaxReached ? "Estoque máximo atingido" : "Adicionar mais um"}
                                                            >
                                                                <Add style={{ fontSize: '15px' }} />
                                                            </button>
                                                        </div>
                                                    );
                                                }

                                                // Múltiplos tamanhos visíveis
                                                const allEsgotados = tamanhosVisiveis.every(t => t.gerenciar_estoque !== false && (t.quantidade_atual ?? 0) <= 0);

                                                if (allEsgotados) {
                                                    return (
                                                        <span className="px-3 py-1 text-xs font-bold text-red-600 bg-red-50 dark:bg-red-950/40 rounded-full border border-red-200 dark:border-red-900">
                                                            Esgotado
                                                        </span>
                                                    );
                                                }

                                                if (quantidade === 0) {
                                                    return (
                                                        <button
                                                            className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold text-xs shadow-sm transition-all active:scale-95 cursor-pointer"
                                                            onClick={(e) => { e.stopPropagation(); setSelectedProduto(produto); }}
                                                        >
                                                            <Add style={{ fontSize: '16px' }} />
                                                            <span>Opções</span>
                                                        </button>
                                                    );
                                                }

                                                return (
                                                    <div className="flex items-center bg-white dark:bg-gray-700 rounded-full p-0.5 shadow-sm border border-gray-200 dark:border-gray-600">
                                                        <button
                                                            className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 flex items-center justify-center transition-colors active:scale-90 cursor-pointer"
                                                            onClick={(e) => handleDecrementMulti(e, produto)}
                                                            title="Diminuir quantidade"
                                                        >
                                                            <Remove style={{ fontSize: '15px' }} />
                                                        </button>
                                                        <span className="px-2.5 font-bold text-sm text-gray-900 dark:text-white min-w-[20px] text-center">
                                                            {quantidade}
                                                        </span>
                                                        <button
                                                            className="w-7 h-7 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white flex items-center justify-center transition-colors shadow-xs active:scale-90 cursor-pointer"
                                                            onClick={(e) => handleIncrementMulti(e, produto)}
                                                            title="Escolher opção para adicionar"
                                                        >
                                                            <Add style={{ fontSize: '15px' }} />
                                                        </button>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                ))}

                {selectedProduto && (
                    <SizeSelectionModal
                        produto={selectedProduto}
                        onClose={() => setSelectedProduto(null)}
                    />
                )}
            </div>

            {/* Preview Rápido / Drawer do Carrinho */}
            <CartDrawer
                isOpen={isCartDrawerOpen}
                onClose={() => setIsCartDrawerOpen(false)}
            />

            {/* Barra Flutuante do Carrinho na Base */}
            <CartBar onOpenDrawer={() => setIsCartDrawerOpen(true)} />
        </>
    );
}